import {
  Grid,
  Box,
  Typography,
  Paper,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  FormHelperText,
  InputAdornment,
} from "@mui/material";
import { useState } from "react";
import { getJaDisplayName } from "components/factoringUtil";
import type { FactoringEntryInput } from "components/factoringModel";
import FileUpload from "components/FileUpload/FileUpload";
import CompanySearchModal from "components/houjinApi/CompanySearchModal";
import CompanySearchByNumberModal from "components/houjinApi/CompanySearchByNumberModal";
import type { CompanyInfo } from "components/houjinApi/companyInfoApi";
import PostalCodeSearchModal from "components/zipCloudApi/PostalCodeSearchModal";
import type { AddressInfoByPostalCode } from "components/zipCloudApi/postalCodeApi";
import AnimationWrapper from "components/AnimationWrapper";
import IsShowField from "components/IsShowField";

interface EntryDetailProps {
  entry: FactoringEntryInput;
  onChange: (newEntry: FactoringEntryInput) => void;
  onRemove: () => void;
  errors?: Record<string, string>;
  entryIndex: number;
  canShow: (fieldName: string, entryIndex?: number, entryField?: string) => boolean;
}

export default function EntryDetail({ entry, onChange, onRemove, errors = {}, entryIndex, canShow }: EntryDetailProps) {
  // Handle field changes
  const handleChange = (field: keyof FactoringEntryInput, value: string | boolean | number) => {
    onChange({
      ...entry,
      [field]: value,
    });
  };

  // Helper function to get error for a specific field
  const getFieldError = (field: keyof FactoringEntryInput) => {
    const errorKey = `entry_${entryIndex}_${field}`;
    return errors[errorKey] || "";
  };

  // State for company search modal
  const [isCompanySearchModalOpen, setIsCompanySearchModalOpen] = useState(false);

  // State for company search by number modal
  const [isCompanySearchByNumberModalOpen, setIsCompanySearchByNumberModalOpen] = useState(false);

  // State for postal code search modal
  const [isPostalCodeSearchModalOpen, setIsPostalCodeSearchModalOpen] = useState(false);

  // Handle company selection from modal
  const handleCompanySelect = (company: CompanyInfo) => {
    onChange({
      ...entry,
      companyName: company.clientCompanyName,
      corporateNumber: company.corporateNumber,
      postalCode: company.postalCode,
      prefecture: company.prefecture,
      city: company.city,
      address1: company.address1,
    });
  };

  // Handle address selection from postal code search modal
  const handleAddressSelect = (address: AddressInfoByPostalCode) => {
    onChange({
      ...entry,
      postalCode: address.postalCode,
      prefecture: address.prefecture,
      city: address.city,
      address1: address.address1,
    });
  };

  // Display the entry details in a paper component with editable fields
  return (
    <>
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h6">
            請求先情報 #{entryIndex + 1}
          </Typography>
          <Button
            color="error"
            variant="text"
            onClick={onRemove}
          >
            削除
          </Button>
        </Box>
        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          <IsShowField canShow={canShow('', entryIndex, 'companyName')}>
            <Grid size={12}>
              <AnimationWrapper delay={0}>
                <TextField
                  id={`company-name-${entryIndex}`}
                  fullWidth
                  required
                  margin="normal"
                  label={getJaDisplayName("companyName")}
                  value={entry.companyName}
                  onChange={(e) => handleChange("companyName", e.target.value)}
                  error={!!getFieldError("companyName")}
                  helperText={
                    getFieldError("companyName") || (
                      <>
                        正式名称で入力してください（例：サンプル株式会社）<br />
                        自動入力したい場合は検索ボタンから
                      </>
                    )
                  }
                  slotProps={{
                    inputLabel: {
                      shrink: true
                    },
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => setIsCompanySearchModalOpen(true)}
                          >
                            法人名検索
                          </Button>
                        </InputAdornment>
                      )
                    }
                  }}
                />
              </AnimationWrapper>
            </Grid>
          </IsShowField>
          <IsShowField canShow={canShow('', entryIndex, 'corporateNumber')}>
            <Grid size={12}>
              <AnimationWrapper>
                <TextField
                  id={`corporate-number-${entryIndex}`}
                  fullWidth
                  margin="normal"
                  label={getJaDisplayName("corporateNumber")}
                  value={entry.corporateNumber}
                  onChange={(e) => handleChange("corporateNumber", e.target.value)}
                  slotProps={{
                    htmlInput: {
                      pattern: "^\\d{13}$",
                      inputMode: "numeric",
                      maxLength: 13,
                    },
                    inputLabel: {
                      shrink: true,
                    },
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => setIsCompanySearchByNumberModalOpen(true)}
                          >
                            法人番号検索
                          </Button>
                        </InputAdornment>
                      )
                    }
                  }}
                  error={!!getFieldError("corporateNumber")}
                  helperText={
                    getFieldError("corporateNumber") || (
                      <>13桁の数字<br />自動入力したい場合は検索ボタンから</>
                    )
                  }
                />
              </AnimationWrapper>
            </Grid>
          </IsShowField>
          <IsShowField canShow={canShow('', entryIndex, 'postalCode')}>
            <Grid size={12}>
              <AnimationWrapper>
                <TextField
                  id={`postal-code-${entryIndex}`}
                  fullWidth
                  margin="normal"
                  label={getJaDisplayName("postalCode")}
                  value={entry.postalCode}
                  onChange={(e) => handleChange("postalCode", e.target.value)}
                  error={!!getFieldError("postalCode")}
                  helperText={<>
                    7桁の数字（ハイフンあり・なし両方可）<br />
                    自動入力したい場合は検索ボタンから<br />
                    ※この検索により以下の項目が入力されます<br />
                    {getJaDisplayName("postalCode")}、
                    {getJaDisplayName("prefecture")}、
                    {getJaDisplayName("city")}、
                    {getJaDisplayName("address1")}
                    {getFieldError("postalCode")}
                  </>}
                  slotProps={{
                    htmlInput: {
                      pattern: "^\\d{3}-?\\d{4}$",
                      inputMode: "numeric",
                      maxLength: 8,
                    },
                    inputLabel: { shrink: true },
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => setIsPostalCodeSearchModalOpen(true)}
                          >
                            郵便番号検索
                          </Button>
                        </InputAdornment>
                      )
                    }
                  }}
                />
              </AnimationWrapper>
            </Grid>
          </IsShowField>
          <IsShowField canShow={canShow('', entryIndex, 'prefecture')}>
            <Grid size={12}>
              <AnimationWrapper>
                <TextField
                  id={`prefecture-${entryIndex}`}
                  fullWidth
                  required
                  margin="normal"
                  label={getJaDisplayName("prefecture")}
                  value={entry.prefecture}
                  onChange={(e) => handleChange("prefecture", e.target.value)}
                  error={!!getFieldError("prefecture")}
                  helperText={getFieldError("prefecture")}
                />
              </AnimationWrapper>
            </Grid>
          </IsShowField>
          <IsShowField canShow={canShow('', entryIndex, 'city')}>
            <Grid size={12}>
              <AnimationWrapper>
                <TextField
                  id={`city-${entryIndex}`}
                  fullWidth
                  required
                  margin="normal"
                  label={getJaDisplayName("city")}
                  value={entry.city}
                  onChange={(e) => handleChange("city", e.target.value)}
                  error={!!getFieldError("city")}
                  helperText={getFieldError("city")}
                />
              </AnimationWrapper>
            </Grid>
          </IsShowField>
          <IsShowField canShow={canShow('', entryIndex, 'address1')}>
            <Grid size={12}>
              <AnimationWrapper>
                <TextField
                  id={`address1-${entryIndex}`}
                  fullWidth
                  required
                  margin="normal"
                  label={getJaDisplayName("address1")}
                  value={entry.address1}
                  onChange={(e) => handleChange("address1", e.target.value)}
                  error={!!getFieldError("address1")}
                  helperText={getFieldError("address1")}
                />
              </AnimationWrapper>
            </Grid>
          </IsShowField>
          <IsShowField canShow={canShow('', entryIndex, 'address2')}>
            <Grid size={12}>
              <AnimationWrapper>
                <TextField
                  id={`address2-${entryIndex}`}
                  fullWidth
                  margin="normal"
                  label={getJaDisplayName("address2")}
                  value={entry.address2}
                  onChange={(e) => handleChange("address2", e.target.value)}
                  error={!!getFieldError("address2")}
                  helperText={getFieldError("address2")}
                />
              </AnimationWrapper>
            </Grid>
          </IsShowField>

          <IsShowField canShow={canShow('', entryIndex, 'billingAmount')}>
            <Grid size={12}>
              <AnimationWrapper>
                <TextField
                  id={`billing-amount-${entryIndex}`}
                  fullWidth
                  required
                  margin="normal"
                  label={getJaDisplayName("billingAmount")}
                  value={entry.billingAmount.toString()}
                  onChange={(e) => {
                    const onlyDigits = e.target.value.replace(/\D+/g, "");
                    const num = onlyDigits ? Number(onlyDigits) : 0;
                    handleChange("billingAmount", num);
                  }}
                  error={!!getFieldError("billingAmount")}
                  helperText={getFieldError("billingAmount") || "数字のみ入力可能"}
                  slotProps={{
                    input: {
                      endAdornment: <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>円</Typography>,
                    },
                    htmlInput: {
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                      min: 0,
                      max: 9999999999
                    }
                  }}
                />
              </AnimationWrapper>
            </Grid>
          </IsShowField>

          <IsShowField canShow={canShow('', entryIndex, 'paymentDate')}>
            <Grid size={12}>
              <AnimationWrapper>
                <TextField
                  id={`payment-date-${entryIndex}`}
                  fullWidth
                  required
                  margin="normal"
                  label={getJaDisplayName("paymentDate")}
                  value={entry.paymentDate}
                  onChange={(e) => handleChange("paymentDate", e.target.value)}
                  error={!!getFieldError("paymentDate")}
                  helperText={getFieldError("paymentDate")}
                  type="date"
                  slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput: {
                      min: "1900-01-01",
                      max: "9999-12-31"
                    }
                  }}
                />
              </AnimationWrapper>
            </Grid>
          </IsShowField>

          <IsShowField canShow={canShow('', entryIndex, 'isInvoiceIssued')}>
            <Grid size={12}>
              <AnimationWrapper>
                <FormControl fullWidth margin="normal">
                  <InputLabel id={`invoice-issued-label-${entryIndex}`}>{getJaDisplayName("isInvoiceIssued")}</InputLabel>
                  <Select
                    id={`invoice-issued-${entryIndex}`}
                    labelId={`invoice-issued-label-${entryIndex}`}
                    value={entry.isInvoiceIssued ? "true" : "false"}
                    label={getJaDisplayName("isInvoiceIssued")}
                    onChange={(e) => handleChange("isInvoiceIssued", e.target.value === "true")}
                  >
                    <MenuItem value="true">請求済</MenuItem>
                    <MenuItem value="false">未請求</MenuItem>
                  </Select>
                  <FormHelperText>申し訳ございませんが未請求の請求書は買取できません</FormHelperText>
                </FormControl>
                {getFieldError("isInvoiceIssued") && (
                  <Typography color="error" variant="caption" sx={{ display: "block", mt: 1 }}>
                    {getFieldError("isInvoiceIssued")}
                  </Typography>
                )}
              </AnimationWrapper>
            </Grid>
          </IsShowField>

          <IsShowField canShow={canShow('', entryIndex, 'imageFiles')}>
            <Grid size={12} id={`grid-files-${entryIndex}`}>
              <AnimationWrapper>
                <Typography
                  variant="subtitle2"
                >
                  {getJaDisplayName("images")}
                  <span className="MuiFormLabel-asterisk">*</span>
                </Typography>
                <Typography variant="caption">
                  ※請求書（あれば、注文書、契約書）を提出ください<br />
                  ※口座履歴（3か月分）（当日までの履歴）（当日履歴がない場合は少額の入出金を行い、履歴を作成ください）また、法人口座（個人事業主の方はすべて）はすべて提出ください<br />
                </Typography>
                <FileUpload
                  onFilesChange={(files) => onChange({ ...entry, imageFiles: files })}
                  files={entry.imageFiles}
                  title=""
                  maxFiles={50}
                  allowedExtensions={["jpg", "jpeg", "png", "pdf"]}
                  restrictionText="※ jpg, jpeg, png, pdf形式のみ、1ファイル10MBまで、最大50ファイルまで"
                />
                {getFieldError("imageFiles") && (
                  <Typography color="error" variant="caption" sx={{ display: "block", mt: 1 }}>
                    {getFieldError("imageFiles")}
                  </Typography>
                )}
              </AnimationWrapper>
            </Grid>
          </IsShowField>
        </Grid>
      </Paper>

      {/* Company Search Modal */}
      <CompanySearchModal
        open={isCompanySearchModalOpen}
        onClose={() => setIsCompanySearchModalOpen(false)}
        onSelect={handleCompanySelect}
        initialSearchTerm={entry.companyName}
        subTitle={<Typography variant="caption">
          ※法人種別を省いた名称を入力して検索してください。例：株式会社サンプルを検索したい場合、サンプルで検索<br />
          ※この検索により以下の項目が入力されます<br />
          {getJaDisplayName("companyName")}、
          {getJaDisplayName("corporateNumber")}、
          {getJaDisplayName("postalCode")}、
          {getJaDisplayName("prefecture")}、
          {getJaDisplayName("city")}、
          {getJaDisplayName("address1")}
        </Typography>}
      />

      {/* Company Search By Number Modal */}
      <CompanySearchByNumberModal
        open={isCompanySearchByNumberModalOpen}
        onClose={() => setIsCompanySearchByNumberModalOpen(false)}
        onSelect={handleCompanySelect}
        initialSearchTerm={entry.corporateNumber}
        subTitle={<Typography variant="caption">
          ※この検索により以下の項目が入力されます<br />
          {getJaDisplayName("companyName")}、
          {getJaDisplayName("corporateNumber")}、
          {getJaDisplayName("postalCode")}、
          {getJaDisplayName("prefecture")}、
          {getJaDisplayName("city")}、
          {getJaDisplayName("address1")}
        </Typography>}
      />

      {/* Postal Code Search Modal */}
      <PostalCodeSearchModal
        open={isPostalCodeSearchModalOpen}
        onClose={() => setIsPostalCodeSearchModalOpen(false)}
        onSelect={handleAddressSelect}
        initialSearchTerm={entry.postalCode}
        subTitle={<Typography variant="caption">
          ※この検索により以下の項目が入力されます<br />
          {getJaDisplayName("postalCode")}、
          {getJaDisplayName("prefecture")}、
          {getJaDisplayName("city")}、
          {getJaDisplayName("address1")}
        </Typography>}
      />
    </>
  );
}
